<template>
    <ModalWrapper v-model="isShowModal" :max-width="350">
        <template v-slot:title>
            {{ $t('adding_elements') }}
        </template>
        <template v-slot:content>
            <v-form ref="form" v-model="valid">
                <v-select
                    v-model="selectedCategory"
                    :items="useAvailableRootRules"
                    :label="`${$t('category')}*`"
                    data-cy="root-rules"
                    :rules="[requiredRule('selectedCategory')]"
                    @update:model-value="updateData"
                />
                <template v-if="selectedCategory === 'productTypeRule'">
                    <v-select
                        v-model="selectedItems"
                        :items="useProducts"
                        item-title="name"
                        item-value="code"
                        :label="`${$t('product_type')}*`"
                        data-cy="product-type"
                        hide-details="auto"
                        multiple
                        return-object
                        :rules="[requiredArrayRule('product_type')]"
                        @update:model-value="handleSelectionChange"
                    />
                </template>
                <template v-if="selectedCategory === 'supplierRule'">
                    <v-select
                        v-model="selectedItems"
                        :items="useSuppliers"
                        item-title="name"
                        item-value="code"
                        :label="`${$t('filterTitles.supplierName')}*`"
                        data-cy="supplier"
                        hide-details="auto"
                        multiple
                        return-object
                        :rules="[requiredArrayRule('category')]"
                        @update:model-value="handleSelectionChange"
                    />
                </template>
                <template v-if="selectedCategory === 'priceRangeRule'">
                    <v-row no-gutters class="gc-2">
                        <v-number-input
                            v-model="priceRangeFrom"
                            :min="0"
                            hide-details
                            :label="$t('transfers.from')"
                            data-cy="price-range-from"
                            :rules="
                                !priceRangeTo && !priceRangeFrom
                                    ? [requiredRule('transfers.from')]
                                    : priceRangeTo && priceRangeFrom && priceRangeTo < priceRangeFrom
                                      ? [priceRangeRule]
                                      : []
                            "
                        />
                        <v-number-input
                            v-model="priceRangeTo"
                            :min="0"
                            hide-details
                            :label="$t('transfers.to')"
                            data-cy="price-range-to"
                            :rules="
                                !priceRangeTo && !priceRangeFrom
                                    ? [requiredRule('transfers.to')]
                                    : priceRangeTo && priceRangeFrom && priceRangeTo < priceRangeFrom
                                      ? [priceRangeRule]
                                      : []
                            "
                        />
                    </v-row>
                </template>
                <template v-if="selectedCategory === 'tariffTagRule'">
                    <v-text-field
                        v-model="ruleValue"
                        hide-details
                        :label="$t('tag_name')"
                        data-cy="tariff-tag-rule"
                        :rules="[requiredRule('tag_name')]"
                    />
                </template>
                <template v-if="selectedCategory === 'accommodationHotelCategoryRule'">
                    <v-select
                        v-model="selectedItems"
                        :items="useCategoryDictionary"
                        item-title="name"
                        item-value="code"
                        :label="`${$t('category')}*`"
                        data-cy="category"
                        hide-details="auto"
                        multiple
                        return-object
                        :rules="[requiredArrayRule('category')]"
                        @update:model-value="handleSelectionChange"
                    />
                </template>
                <template v-if="selectedCategory === 'countryRule'">
                    <v-autocomplete
                        v-model="ruleValue"
                        :label="$t('country')"
                        :items="useCountries"
                        item-title="name"
                        item-value="id"
                        data-cy="country"
                        hide-details="auto"
                        return-object
                        :rules="[requiredRule('country')]"
                    />
                </template>
                <template v-if="selectedCategory === 'cityRule'">
                    <v-autocomplete
                        v-model="selectedCountry"
                        :label="$t('country')"
                        :items="useCountries"
                        item-title="name"
                        item-value="id"
                        data-cy="city-rule-country"
                        clearable
                        :rules="[requiredRule('country')]"
                        :disabled="parentFilters?.countryRule"
                        @update:model-value="updateCities()"
                    />
                    <v-autocomplete
                        v-model="ruleValue"
                        :items="useCities"
                        item-title="name"
                        item-value="id"
                        :label="$t('city')"
                        :loading="isCitiesLoading"
                        :disabled="isCitiesLoading || !useCities.length"
                        closable-chips
                        hide-details="auto"
                        clearable
                        data-cy="city"
                        return-object
                        :rules="[requiredRule('city')]"
                    />
                </template>
            </v-form>
        </template>
        <template v-slot:btns>
            <v-btn :disabled="!valid" color="primary" variant="flat" data-cy="apply" @click="addNewItem">
                {{ $t('apply') }}
            </v-btn>
        </template>
    </ModalWrapper>
</template>
<script setup lang="ts">
    import {requiredArrayRule, requiredRule} from '~/composables/rules'
    import {getMappedProductKeys} from '~/utils/authorization-info-utils'

    const props = defineProps<{
        availableRootRules: any[]
        selectedRoot: any
        availableSuppliers: any[]
        currency?: string
    }>()

    const isShowModal = defineModel<boolean>()
    const valid = ref<boolean>(false)

    const selectedCategory = ref<string>()
    const children = ref<any>([])
    const parentFilters = ref<any>([])

    const ruleValue = ref<any>(null)
    const selectedItems = ref<any>(null)

    const selectedCountry = ref(null)
    const isCitiesLoading = ref<boolean>(false)
    const cities = ref([])

    const priceRangeFrom = ref(null)
    const priceRangeTo = ref(null)

    const suppliers = ref([])
    const availableProducts = ref([])

    const {$api, $conf} = useNuxtApp()
    const {t} = useI18n()

    const emit = defineEmits<{
        (e: 'add-item', value: any)
    }>()

    const handleSelectionChange = () => {
        let items = useProducts.value
        switch (selectedCategory.value) {
            case 'supplierRule':
                items = useSuppliers.value
                break
            case 'productTypeRule':
                items = useProducts.value
                break
            case 'accommodationHotelCategoryRule':
                items = useCategoryDictionary.value
                break
        }

        selectedItems.value = items.filter(item =>
            selectedItems.value.some(selectedItem => selectedItem.code === item.code)
        )
    }

    const resetData = () => {
        ruleValue.value = null
        selectedItems.value = null

        selectedCountry.value = null
        cities.value = []

        priceRangeFrom.value = null
        priceRangeTo.value = null
    }

    watch(isShowModal, async () => {
        if (!isShowModal.value) {
            selectedCategory.value = null
            children.value = []
            parentFilters.value = []

            resetData()
        }

        if (isShowModal.value) {
            selectedCategory.value = useAvailableRootRules.value?.[0]?.value || null
            if (props.selectedRoot?.parentFilters?.length) {
                parentFilters.value = props.selectedRoot?.parentFilters.reduce((acc, item) => {
                    acc[item.ruleToken] = item.ruleValue
                    return acc
                }, {})
            }
            children.value = props.selectedRoot?.pricingRuleEntries || []

            await updateData()

            switch (selectedCategory.value) {
                case 'productTypeRule':
                    if (children.value.length) {
                        selectedItems.value = children.value
                            .filter(({ruleToken}) => ruleToken !== 'other')
                            .map(({ruleValue, label}) => ({code: ruleValue, name: label}))
                    }
                    if (parentFilters.value?.supplierRule) {
                        // await loadAvailableProducts()
                    }
                    break
                case 'supplierRule':
                    if (children.value.length) {
                        selectedItems.value = children.value
                            .filter(({ruleToken}) => ruleToken !== 'other')
                            .map(({ruleValue, label}) => ({code: ruleValue, name: label}))
                    }
                    if (parentFilters.value?.productTypeRule) {
                        await loadSuppliers()
                    }
                    break
                case 'cityRule':
                    if (parentFilters.value?.countryRule) {
                        selectedCountry.value = +parentFilters.value.countryRule
                        await loadCities()
                    }
                    break
                case 'accommodationHotelCategoryRule':
                    if (children.value.length) {
                        selectedItems.value = children.value
                            .filter(({ruleToken}) => ruleToken !== 'other')
                            .map(({ruleValue}) => {
                                const cleanValue = ruleValue.replace('hotelcategory.', '')
                                return {
                                    code: ruleValue,
                                    name: cleanValue === 'other' ? t('no_category') : `${cleanValue}*`,
                                }
                            })
                    }
                    break
            }
        }
    })

    const addNewItem = () => {
        const mapItems = items => items.map(item => ({label: item.name, ruleValue: item.code}))
        let returnedItems
        let dirtyValue

        switch (selectedCategory.value) {
            case 'productTypeRule':
            case 'supplierRule':
            case 'accommodationHotelCategoryRule':
                returnedItems = mapItems(selectedItems.value)
                break
            case 'priceRangeRule':
                dirtyValue = `${priceRangeFrom.value ? priceRangeFrom.value : 0}-${priceRangeTo.value ? priceRangeTo.value : ''}`
                returnedItems = [
                    {
                        label: !priceRangeTo.value
                            ? `${t('price_range')} (${t(`${priceRangeFrom.value}${props?.currency ? ` ${props.currency}` : ''}`)} ${t('or_more')})`
                            : `${t('price_range')} (${t(`${dirtyValue}${props?.currency ? ` ${props.currency}` : ''}`)})`,
                        ruleValue: dirtyValue,
                    },
                ]
                break
            case 'countryRule':
            case 'cityRule':
                returnedItems = [{label: ruleValue.value.name, ruleValue: ruleValue.value.id}]
                break
            default:
                // case 'tariffTagRule'
                returnedItems = [{label: ruleValue.value, ruleValue: ruleValue.value}]
                break
        }

        if (selectedCategory.value && returnedItems.length) {
            emit('add-item', {
                selectedItems: returnedItems,
                ruleToken: selectedCategory.value,
            })
            isShowModal.value = false
        }
    }

    const useAvailableRootRules = computed(() => {
        return props.availableRootRules.map(value => ({value, title: t(`rootRules.${value}`)}))
    })

    async function loadSuppliers(): Promise<void> {
        suppliers.value = (
            await $api.suppliers.get({productType: parentFilters.value.productTypeRule})
        )?.suppliers.sort((a, b) => a.name.localeCompare(b.name))
    }

    const useSuppliers = computed(() => {
        if (parentFilters.value?.productTypeRule) {
            return suppliers.value
        }
        return props.availableSuppliers
    })

    const useCountries = computed(() => {
        if (children.value.length) {
            const notAvailableCountries = children.value.map(({ruleValue}) => +ruleValue)
            return runtimeStore.countries.filter(({id}) => !notAvailableCountries.includes(id))
        }
        return runtimeStore.countries
    })

    const updateData = async () => {
        resetData()

        if (selectedCategory.value === 'accommodationHotelCategoryRule' && !supplierStore.categoryDictionary.length) {
            await supplierStore.loadHotelCategoryDictionaryInfo()
        }
        if (selectedCategory.value === 'cityRule' && parentFilters.value?.countryRule) {
            selectedCountry.value = +parentFilters.value.countryRule
            await loadCities()
        }
    }

    const useCategoryDictionary = computed(() => {
        return supplierStore.categoryDictionary.map(categoryDictionary => {
            return {
                ...categoryDictionary,
                code:
                    categoryDictionary.code === '0'
                        ? 'hotelcategory.other'
                        : `hotelcategory.${categoryDictionary.code}`,
                name: categoryDictionary.code === '0' ? t('no_category') : `${categoryDictionary.code}*`,
            }
        })
    })

    const updateCities = async (): Promise<void> => {
        if (!selectedCountry.value) {
            cities.value = []
            ruleValue.value = null
        } else {
            await loadCities()
        }
    }

    async function loadCities() {
        try {
            isCitiesLoading.value = true
            const rs = await $api.locations.get({
                limitCities: -1,
                orderBy: 'POPULARITY',
                countryId: selectedCountry.value,
            })
            if (rs.cities) {
                cities.value = rs.cities.map(({id, name}) => {
                    return {name, id}
                })
            }
        } catch (e) {
            cities.value = []
        } finally {
            isCitiesLoading.value = false
        }
    }

    const useCities = computed(() => {
        if (children.value.length) {
            const notAvailableCities = children.value.map(({ruleValue}) => +ruleValue)
            return cities.value.filter(({value}) => !notAvailableCities.includes(value))
        }
        return cities.value
    })

    const useProducts = computed(() => {
        // if (parentFilters.value?.supplierRule) {
        //     return availableProducts.value.map(product => ({name: t(`productType.${product}`), code: product}))
        // }
        const mappedProductKeys = getMappedProductKeys($conf.public.activeProducts, authStore.availableProducts)
        return mappedProductKeys.map(product => ({name: t(`productType.${product}`), code: product}))
    })

    async function loadAvailableProducts() {
        try {
            //TODO[p0] add request after implementation in the api
        } catch (e) {
            availableProducts.value = []
        }
    }

    const priceRangeRule = computed(() => {
        return () => t('incorrect_price_range')
    })
</script>
